import {
  AIStarsIcon,
  Button,
  Chevron,
  CollapsibleComponent,
  Disclaimer,
  Div,
  H5,
  Icon,
  LoadingDots,
  Modal,
  P,
  Separator,
  displayCentsInDollars,
  handleError,
  lightTheme,
  useElementHeight,
} from '@vgrubs/components';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import {
  EarningExplanationFaqEntity,
  EarningExplanationLineEntity,
  PaymentReportLineEntity,
} from 'components/src/types/api/wallet.types';
import { useEarningExplanation } from '../hooks/useEarningExplanation';
import { amountColorByValue } from '../TransactonsStyled';
import { NotesBox } from './EarningExplanationModal.styled';

/**
 * Maps a failed explanation fetch to display copy. The two 400s come with
 * messages written for the user (pending payout report / no breakdown), so
 * they are shown as-is; only a 5xx or a network drop earns a Try Again -
 * retrying a 4xx would just fail the same way.
 */
const describeExplanationError = (
  error: unknown
): { message: string; canRetry: boolean } => {
  if (isAxiosError(error) && error.response) {
    const { status } = error.response;
    if (status === 404)
      return {
        message: 'No explanation is available for this order.',
        canRetry: false,
      };
    if (status >= 400 && status < 500)
      return {
        message:
          handleError(error) ||
          'This order has no payout breakdown to explain.',
        canRetry: false,
      };
  }
  return { message: 'AI generation failed.', canRetry: true };
};

/**
 * One booked breakdown line, rendered exactly like IncomeDetailModal's, plus
 * whatever the AI wrote for it once it arrives. An empty explanation leaves
 * the line bare - it is a real amount in the total, so it is never hidden.
 */
const BreakdownLine = ({
  line,
  aiLine,
}: {
  line: PaymentReportLineEntity;
  aiLine?: EarningExplanationLineEntity;
}) => {
  const explanation = aiLine?.explanation.trim();
  return (
    <div>
      <Div $flex $alignCenter $between>
        <P $m="0">{line.label}:</P>
        <P $m="0" $color={amountColorByValue(line.amount)}>
          {displayCentsInDollars(line.amount)}
        </P>
      </Div>
      {explanation && (
        <Disclaimer $m="4px 0 0" $color={lightTheme.lightGray}>
          {explanation}
        </Disclaimer>
      )}
      {aiLine?.note && (
        <Disclaimer $m="2px 0 0" $color={lightTheme.lightGray}>
          Provider note: {aiLine.note}
        </Disclaimer>
      )}
      {line.divider && <Separator $m="8px 0 0" />}
    </div>
  );
};

/**
 * The breakdown always renders from the LOCAL booked components - numbers and
 * dividers appear instantly, and the AI's lines (which mirror them 1:1 by
 * index, without the divider flag) only decorate them once they arrive.
 */
const BreakdownSection = ({
  components,
  lines,
}: {
  components: PaymentReportLineEntity[];
  lines?: EarningExplanationLineEntity[];
}) => {
  if (!components.length) return null;
  return (
    <>
      <Separator $m="12px 0" />
      <H5 $mb="8">Income Breakdown</H5>
      <Div $flex $column $gap="8px">
        {components.map((line, index) => (
          <BreakdownLine
            key={`${line.label}-${index}`}
            line={line}
            aiLine={lines?.[index]}
          />
        ))}
      </Div>
    </>
  );
};

/** One FAQ entry; owns its open state and measured height independently. */
const FaqItem = ({ question, answer }: EarningExplanationFaqEntity) => {
  const [open, setOpen] = useState(false);
  const { ref, height } = useElementHeight<HTMLDivElement>();
  return (
    <div>
      <Div
        $flex
        $between
        $alignCenter
        $gap="16px"
        $pointer
        onClick={() => setOpen((o) => !o)}
      >
        <P $bold $m="0">
          {question}
        </P>
        <Icon
          $pointer
          $rotate={open ? '360deg' : '180deg'}
          color={lightTheme.darkGray}
        >
          <Chevron size={16} />
        </Icon>
      </Div>
      <CollapsibleComponent expanded={open} elementHeight={height ?? 0}>
        {/* Padding on the measured node so the gap animates with the answer;
            user-select restored because the collapsible disables it. */}
        <div ref={ref} style={{ paddingTop: '8px', userSelect: 'text' }}>
          <P $m="0" $color={lightTheme.lightGray}>
            {answer}
          </P>
        </div>
      </CollapsibleComponent>
    </div>
  );
};

/**
 * AI explanation of one order's payout, stacked above IncomeDetailModal
 * (parent overlay/box sit at z 10/11, this one at 20/21).
 *
 * `glow` is passed on every render on purpose: the modal mounts its glow
 * wrapper whenever the prop is present and only animates opacity, so the
 * gradient border fades out when generation finishes instead of remounting
 * the whole modal (see modal.tsx).
 */
export const EarningExplanationModal = ({
  isOpen,
  closeModal,
  orderId,
  components,
}: {
  isOpen: boolean;
  closeModal: () => void;
  /** Null disables the fetch (the parent only opens this when it is set). */
  orderId: number | null | undefined;
  /** The row's own booked lines - rendered immediately, before the AI text. */
  components: PaymentReportLineEntity[];
}) => {
  const { data, error, isGenerating, refetch } = useEarningExplanation({
    orderId,
    enabled: isOpen,
  });

  // Generating wins over a stale error so Try Again visibly goes back to the
  // loading state instead of looking dead for the length of the retry.
  const errorInfo =
    !isGenerating && error ? describeExplanationError(error) : null;

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      logo={false}
      textAlign={'left'}
      zIndex={20}
      glow={isGenerating}
    >
      <Div $flex $alignCenter $gap="8px" $mb="16">
        <Icon>
          <AIStarsIcon />
        </Icon>
        <H5 $m="0">Payout Summary</H5>
      </Div>

      {isGenerating ? (
        <>
          <BreakdownSection components={components} />
          <Div $mt="24">
            <P $m="0" $center $color={lightTheme.lightGray}>
              Generating summary
              <LoadingDots active />
            </P>
            <Disclaimer $m="4px 0 0" $center $color={lightTheme.lightGray}>
              This usually takes a few seconds.
            </Disclaimer>
          </Div>
        </>
      ) : errorInfo ? (
        <>
          <P $m="0">{errorInfo.message}</P>
          {errorInfo.canRetry && (
            <Button
              $blueGradient
              $selected
              $w="100%"
              $mt="16"
              onClick={() => refetch()}
            >
              Try Again
            </Button>
          )}
          <BreakdownSection components={components} />
        </>
      ) : data ? (
        <>
          <P $m="0">{data.summary}</P>
          <BreakdownSection components={components} lines={data.lines} />
          {data.notes.length > 0 && (
            <>
              <Separator $m="12px 0" />
              <H5 $mb="8">Notes</H5>
              <NotesBox>
                {data.notes.map((note, index) => (
                  <Disclaimer key={index} $m={index ? '6px 0 0' : '0'}>
                    {note}
                  </Disclaimer>
                ))}
              </NotesBox>
            </>
          )}
          {data.faq.length > 0 && (
            <>
              <Separator $m="12px 0" />
              <H5 $mb="8">FAQ</H5>
              <Div $flex $column $gap="12px">
                {data.faq.map((item) => (
                  <FaqItem key={item.question} {...item} />
                ))}
              </Div>
            </>
          )}
        </>
      ) : null}

      <Separator $m="12px 0" />
      <Disclaimer $m="0" $color={lightTheme.lightGray}>
        This explanation is AI-generated from the order's booked amounts and may
        simplify provider terminology. The amounts themselves come from the
        provider's payout report.
      </Disclaimer>
    </Modal>
  );
};
